import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { PiBriefcase, PiCalendarBlank, PiTextAlignLeft } from 'react-icons/pi';
import { createTask } from '../../../store/actions/TasksActions';
import Input from '../../shared/TailwindComponents/Input';
import { RootState, useAppDispatch } from '../../../store';
import Button from '../../shared/TailwindComponents/Button';
import FormActions from '../../shared/FormComponents/FormActions';
import { useTranslation } from 'react-i18next';
import { FiLock } from 'react-icons/fi';
import { useSelector } from 'react-redux';

interface AssignTaskFormProps {
  employeeEmail: string;
  onCancel: () => void;
  onAdd: () => void;
}

const AssignTaskForm: React.FC<AssignTaskFormProps> = ({ employeeEmail, onCancel, onAdd }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation()
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);

  const formik = useFormik({
    initialValues: {
      TaskTitle: '',
      TaskDescription: '',
      TaskDeadline: '',
    },
    validationSchema: Yup.object().shape({
      TaskTitle: Yup.string().required(t('Title is required')),
      TaskDescription: Yup.string().required(t('Description is required')),
      TaskDeadline: Yup.date().required(t('Deadline is required')),
    }),
    onSubmit: async (values) => {
      try {
        const taskData = {
          TaskTitle: values.TaskTitle,
          TaskDescription: values.TaskDescription,
          TaskDeadline: values.TaskDeadline,
          AssigneeEmail: employeeEmail,
        };
        await dispatch(createTask(taskData));
        onAdd();
      } catch (error) {
        console.error('Error creating task', error);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <Input
        label={t("Task title")}
        type="text"
        name="TaskTitle"
        value={formik.values.TaskTitle}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.TaskTitle && formik.errors.TaskTitle ? t(formik.errors.TaskTitle) : undefined}
        required
        icon={<PiBriefcase className="text-fg-muted" size={18} />}
      />
      <Input
        label={t("Task description")}
        type="text"
        name="TaskDescription"
        value={formik.values.TaskDescription}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.TaskDescription && formik.errors.TaskDescription ? t(formik.errors.TaskDescription) : undefined}
        required
        icon={<PiTextAlignLeft className="text-fg-muted" size={18} />}
      />
      <Input
        label={t("Task deadline")}
        type="date"
        name="TaskDeadline"
        value={formik.values.TaskDeadline}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.TaskDeadline && formik.errors.TaskDeadline ? t(formik.errors.TaskDeadline) : undefined}
        required
        icon={<PiCalendarBlank className="text-fg-muted" size={18} />}
      />
      <FormActions>
        <Button variant="neutral" size="small" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button
          variant="primary"
          size="small"
          type="submit"
          className="relative"
          disabled={!subscriptionActive }
        >
          {t("Assign task")}

          {!subscriptionActive && (
            <FiLock
              className="absolute -top-2 -end-2 text-warning-500 bg-surface rounded-full shadow-md p-[2px]"
              size={16}
              title={t("Premium feature")}
            />
          )}
        </Button>
      </FormActions>
    </form>
  );
};

export default AssignTaskForm;